import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown } from "lucide-react";

/**
 * Autocomplete input reutilizable.
 * Props:
 *   value: string
 *   onChange: (nuevo) => void
 *   options: string[]
 *   placeholder
 *   className
 *   allowFreeText (bool, default true) — si false, fuerza a elegir de la lista
 *   testId
 */
export default function AutocompleteInput({
  value = "",
  onChange,
  options = [],
  placeholder = "Escribe para buscar…",
  className = "",
  allowFreeText = true,
  testId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const norm = (s) => (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ").trim();

  const filtered = React.useMemo(() => {
    const q = norm(query);
    if (!q) return options.slice(0, 25);
    return options.filter((o) => norm(o).includes(q)).slice(0, 25);
  }, [query, options]);

  const isExact = filtered.some((o) => norm(o) === norm(query));

  const commit = (v) => {
    setQuery(v);
    onChange?.(v);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (allowFreeText) onChange?.(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          className="bg-zinc-950 border-zinc-800 pr-8"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto bg-zinc-950 border border-zinc-800 rounded shadow-xl">
          {filtered.map((o) => (
            <button
              type="button"
              key={o}
              onClick={() => commit(o)}
              data-testid={testId ? `${testId}-opt-${norm(o).replace(/\s/g, "-")}` : undefined}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-800 flex items-center justify-between
                ${norm(o) === norm(query) ? "text-emerald-300" : "text-zinc-100"}`}
            >
              <span>{o}</span>
              {norm(o) === norm(query) && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          {allowFreeText && query && !isExact && (
            <button
              type="button"
              onClick={() => commit(query)}
              className="w-full text-left px-3 py-1.5 text-xs border-t border-zinc-800 text-amber-300 hover:bg-zinc-800"
            >
              + Usar &quot;{query}&quot; (nuevo)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
